import { useState, useEffect } from 'react';
import {
    Dialog,
    DialogContent,
    DialogTitle,
    Stack,
    Typography,
    Box,
    InputLabel,
    Button,
    FormControlLabel,
} from '@mui/material';
import { withStyles } from '@mui/styles';
import moment from 'moment';
import { useForm, Controller } from 'react-hook-form-v7';
import Switch from '../../../Shared/Components/Switch';
import { COLORS, DATE_FORMATS } from '../../../utils/Application_Constants';
import useGenerateInvoices from '../../hooks/useGenerateInvoices';
import ConnectedDatePicker from '../ProcessedTimesheets/ConnectedDatePicker';
import { useInvoicingSelectionContext } from '../../Contexts/InvoicingSelectionContext';
import formatDate from '../../../utils/formatDate';

const styles = () => ({
    datePicker: {
        '& .MuiPickersSectionList-root': {
            padding: '13px 0',
            fontWeight: 'normal',
            fontSize: 14,
        },
        '& .MuiPickersOutlinedInput-notchedOutline': {
            borderColor: COLORS.LT_MIDNIGHT10,
        },
        '& .MuiFormHelperText-root': {
            marginLeft: 0,
            position: 'absolute',
            top: '100%',
        },
        '&.MuiPickersTextField-root': {
            marginBottom: 0,
        },
    },
    formLabel: {
        marginTop: 12,
        '& .MuiTypography-root': {
            fontSize: 14,
        },
    },
});

const InvoiceInitiationConfirmationModal = ({
    open,
    onClose,
    classes,
    action,
    doNotSendToClient,
    setDoNotSendToClient,
    setSuccessState,
    refetch,
}) => {
    const initializeFormData = () => ({
        postingDate: moment(Date.now()),
        invoiceDate: moment(Date.now()),
        doNotSendToClient: doNotSendToClient ?? false,
    });
    const [submissionError, setSubmissionError] = useState('');

    const { selectedItemsCount, selectedItems, deselectAllItems } = useInvoicingSelectionContext();

    const { control, handleSubmit, reset } = useForm({
        defaultValues: initializeFormData(),
        mode: 'onBlur',
    });

    useEffect(() => {
        if (open) {
            reset(initializeFormData());
            setSubmissionError('');
        }
    }, [open, reset]);

    const { mutate: generateInvoices, isLoading: generateInvoicesLoading } = useGenerateInvoices({
        onMutate: () => {
            setSuccessState({ showAlert: false, count: 0, action: '' });
            setSubmissionError('');
        },
        onSuccess: () => {
            setSuccessState({ showAlert: true, count: selectedItemsCount, action });
            deselectAllItems();
            onClose();
            refetch();
        },
        onError: (error) => {
            console.error('useGenerateInvoices:', error);
            setSubmissionError(`There was an error generating the ${action}s. Please try again.`);
        },
    });

    const onSubmit = (formData) => {
        const dataToSubmit = {
            ...formData,
            invoiceDate: formatDate(formData.invoiceDate.toDate(), {
                format: DATE_FORMATS.ISO_8601,
            }),
            postingDate: formatDate(formData.postingDate.toDate(), {
                format: DATE_FORMATS.ISO_8601,
            }),
            toInvoice: selectedItems,
            preInvoice: action === 'pre-invoice',
        };

        generateInvoices(dataToSubmit);
    };

    const handleFormSubmit = (event) => {
        event.preventDefault();
        handleSubmit(onSubmit, () => {
            console.error('Validtion Errors');
        })();
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullWidth
            slotProps={{ paper: { sx: { borderRadius: '8px', padding: '30px' } } }}
        >
            <DialogTitle>
                <Typography variant="heading3" sx={{ color: COLORS.BLACK }}>
                    Confirm {action === 'pre-invoice' ? 'Pre-' : ''}Invoices
                </Typography>
            </DialogTitle>
            <DialogContent>
                <Typography variant="body2">
                    You&apos;ve selected {selectedItemsCount} unbilled item(s). Do you wish to
                    process them?
                </Typography>
                <form onSubmit={(e) => handleFormSubmit(e)}>
                    <Stack
                        sx={{
                            border: `1px solid ${COLORS.LT_MIDNIGHT10} `,
                            mt: 2,
                            p: 1,
                            borderRadius: '8px',
                        }}
                        gap={2}
                    >
                        <Typography
                            variant="heading5"
                            sx={{ fontWeight: 'bold', color: COLORS.BLACK }}
                        >
                            Verify Dates
                        </Typography>
                        <Typography variant="body2">
                            The Posting Date affects your accounting records, and the Invoice Date
                            is the official client issue date. Please confirm.
                        </Typography>
                        <Stack direction="row" gap={2}>
                            <Box sx={{ mr: 2 }}>
                                <InputLabel sx={{ fontSize: 14, mb: 1 }} id="rate-type-label">
                                    Posting Date
                                </InputLabel>
                                <ConnectedDatePicker
                                    control={control}
                                    name="postingDate"
                                    pickerProps={{
                                        format: 'MM/DD/YYYY',
                                        className: classes.datePicker,
                                    }}
                                    rules={{ required: 'Posting Date is required.' }}
                                />
                            </Box>
                            <Box sx={{ mr: 2 }}>
                                <InputLabel sx={{ fontSize: 14, mb: 1 }} id="rate-type-label">
                                    Invoice Date
                                </InputLabel>
                                <ConnectedDatePicker
                                    control={control}
                                    name="invoiceDate"
                                    pickerProps={{
                                        format: 'MM/DD/YYYY',
                                        className: classes.datePicker,
                                    }}
                                    rules={{ required: 'Invoice Date is required.' }}
                                />
                            </Box>
                        </Stack>
                    </Stack>
                    <Controller
                        control={control}
                        name="doNotSendToClient"
                        render={({ field: { onChange, value, ...rest } }) => (
                            <FormControlLabel
                                className={classes.formLabel}
                                control={
                                    <Switch
                                        checked={value}
                                        onChange={() => {
                                            onChange(!value);
                                            setDoNotSendToClient(!value);
                                        }}
                                        {...rest}
                                        name="doNotSendToClient"
                                    />
                                }
                                label="Do not send to client"
                            />
                        )}
                    />
                    <Stack direction="row" sx={{ mt: 4 }}>
                        <Button variant="outlined" color="primary" sx={{ mr: 2 }} onClick={onClose}>
                            Cancel
                        </Button>
                        <Button
                            variant="contained"
                            color="primary"
                            type="submit"
                            loading={generateInvoicesLoading}
                            sx={{ ml: 'auto' }}
                        >
                            Create {action === 'pre-invoice' ? 'Pre-' : ''}Invoices
                        </Button>
                    </Stack>
                    {submissionError && (
                        <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                            {submissionError}
                        </Typography>
                    )}
                </form>
            </DialogContent>
        </Dialog>
    );
};

export default withStyles(styles)(InvoiceInitiationConfirmationModal);
